// File: components/SkyDrop.tsx
import React, { useState, useEffect, useRef } from 'react';
import { CITIES } from '../constants';
import { drawRumi } from './renderer/player/drawRumi';
import { drawMira } from './renderer/allies/drawMira';
import { drawZoey } from './renderer/allies/drawZoey';
import { FastForward, X } from 'lucide-react';
import { ExitButton } from './ExitButton';

interface SkyDropProps {
  destination: string;
  onComplete: () => void;
}

type CameraPhase = 'FORMATION' | 'FOCUS_RUMI' | 'FOCUS_MIRA' | 'FOCUS_ZOEY' | 'WIDE_WIDE';

const START_ALTITUDE = 25000;

export const SkyDrop: React.FC<SkyDropProps> = ({ destination, onComplete }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // HUD State (Updates UI only)
  const [displayAltitude, setDisplayAltitude] = useState(START_ALTITUDE);
  
  // Animation Refs (Ensures smooth, persistent 60fps loop without React re-renders)
  const altitudeRef = useRef(START_ALTITUDE);
  const velocityRef = useRef(420);
  const phaseRef = useRef<CameraPhase>('FORMATION');
  
  const destName = CITIES[destination]?.name || destination;

  // Stable Cinematic Timer
  useEffect(() => {
    // Cinematic Sequence: Formation -> Mira (Pink/Dancer) -> Wide -> Rumi (Leader) -> Formation -> Zoey (Black/Rapper)
    const phases: CameraPhase[] = [
      'FORMATION', 
      'FOCUS_ZOEY',  // Focus on Zoey first now
      'WIDE_WIDE', 
      'FOCUS_RUMI', 
      'FORMATION', 
      'FOCUS_MIRA'
    ];
    let currentIdx = 0;
    const phaseTimer = setInterval(() => {
        currentIdx = (currentIdx + 1) % phases.length;
        phaseRef.current = phases[currentIdx];
    }, 1500); // Much faster camera cuts
    return () => clearInterval(phaseTimer);
  }, []);

  // Stable Physics Timer
  useEffect(() => {
    const dropTimer = setInterval(() => {
      altitudeRef.current -= 800; // Much faster descent
      velocityRef.current = Math.min(580, velocityRef.current + 0.5);
      
      // Sync to UI state less frequently for performance
      setDisplayAltitude(Math.max(0, altitudeRef.current));

      if (altitudeRef.current <= 500) {
        clearInterval(dropTimer);
        onComplete();
      }
    }, 100);
    return () => clearInterval(dropTimer);
  }, [onComplete]);

  // Main Animation Loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Generate persistent visual assets
    const windTrails = Array.from({ length: 15 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      length: 800 + Math.random() * 1200,
      speed: 40 + Math.random() * 20,
      opacity: 0.1,
      width: 1 + Math.random() * 2
    }));

    const clouds = Array.from({ length: 8 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: 300 + Math.random() * 400,
      opacity: 0.05,
      speed: 10 + Math.random() * 15
    }));

    let requestID: number;
    // Initial Camera Center: Shifted even further UP to 22% height
    let cameraLerpX = canvas.width / 2;
    let cameraLerpY = canvas.height * 0.22;
    let cameraLerpScale = 1.0;

    const animate = () => {
      const centerX = canvas.width / 2;
      const centerY = canvas.height * 0.22; // Action center anchored high
      const time = Date.now();
      const currentAltitude = altitudeRef.current;
      const currentPhase = phaseRef.current;

      // 1. Background Gradient
      const skyProgress = currentAltitude / START_ALTITUDE;
      const bgGrad = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, canvas.width);
      bgGrad.addColorStop(0, '#0c4a6e'); 
      bgGrad.addColorStop(0.7, '#020617'); 
      bgGrad.addColorStop(1, '#000000');
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 2. Distant Tactical Grid
      ctx.save();
      ctx.globalAlpha = 0.08 * (1 - skyProgress + 0.2);
      ctx.strokeStyle = '#22d3ee';
      ctx.lineWidth = 1;
      ctx.translate(centerX, centerY);
      ctx.rotate(time * 0.00005);
      for(let i=0; i<8; i++) {
        ctx.beginPath();
        ctx.arc(0, 0, (i * 300 + time * 0.1) % 2400, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.restore();

      // 3. Smooth Clouds
      clouds.forEach(c => {
        c.y -= c.speed;
        if (c.y < -c.size) {
          c.y = canvas.height + c.size;
          c.x = Math.random() * canvas.width;
        }
        ctx.fillStyle = `rgba(255, 255, 255, ${c.opacity})`;
        ctx.beginPath();
        ctx.arc(c.x, c.y, c.size, 0, Math.PI * 2);
        ctx.fill();
      });

      // 4. Smooth Wind Trails
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      windTrails.forEach(t => {
        t.y -= t.speed;
        if (t.y < -t.length) {
          t.y = canvas.height + t.length;
          t.x = Math.random() * canvas.width;
        }
        const trailGrad = ctx.createLinearGradient(0, t.y, 0, t.y + t.length);
        trailGrad.addColorStop(0, 'rgba(34, 211, 238, 0)');
        trailGrad.addColorStop(0.5, `rgba(34, 211, 238, ${t.opacity})`);
        trailGrad.addColorStop(1, 'rgba(34, 211, 238, 0)');
        ctx.fillStyle = trailGrad;
        ctx.fillRect(t.x, t.y, t.width, t.length);
      });
      ctx.restore();

      // 5. Camera & Characters
      ctx.save();
      const rumiPos = { x: centerX, y: centerY }; 
      const zoeyPos = { x: centerX - 280, y: centerY - 150 }; // Swapped: Zoey is Left
      const miraPos = { x: centerX + 280, y: centerY - 150 }; // Swapped: Mira is Right

      let targetX = centerX;
      let targetY = centerY;
      let targetScale = 1.0;

      switch(currentPhase) {
          case 'FOCUS_RUMI':
              targetX = rumiPos.x; targetY = rumiPos.y - 120; targetScale = 2.6; break;
          case 'FOCUS_MIRA':
              targetX = miraPos.x; targetY = miraPos.y - 120; targetScale = 2.4; break;
          case 'FOCUS_ZOEY':
              targetX = zoeyPos.x; targetY = zoeyPos.y - 120; targetScale = 2.4; break;
          case 'WIDE_WIDE':
              targetScale = 0.5; break;
      }

      // Smooth Camera Interpolation
      cameraLerpX += (targetX - cameraLerpX) * 0.05;
      cameraLerpY += (targetY - cameraLerpY) * 0.05;
      cameraLerpScale += (targetScale - cameraLerpScale) * 0.05;

      ctx.translate(centerX, centerY);
      ctx.scale(cameraLerpScale, cameraLerpScale);
      ctx.translate(-cameraLerpX, -cameraLerpY);

      const drawSuitMember = (drawFn: any, x: number, y: number, roll: number, isRumi: boolean = false) => {
          ctx.save();
          ctx.translate(x, y);
          ctx.rotate(-Math.PI / 2 + roll);
          
          const aura = ctx.createLinearGradient(0, 0, 0, -300);
          aura.addColorStop(0, 'rgba(34, 211, 238, 0.2)');
          aura.addColorStop(1, 'transparent');
          ctx.fillStyle = aura;
          ctx.fillRect(-25, -300, 50, 300);

          drawFn(ctx, { 
              x: -45, y: -220, width: 90, height: 220, 
              vx: 0, isSwinging: false, swingTimer: 0, facing: 1 as (1 | -1),
              color: isRumi ? '#facc15' : '#ffffff' 
          });

          ctx.globalCompositeOperation = 'screen';
          ctx.shadowBlur = 15;
          ctx.shadowColor = '#22d3ee';
          ctx.fillStyle = '#22d3ee';
          ctx.fillRect(-18, -188, 36, 4);
          ctx.restore();
      };

      const rollRumi = Math.sin(time / 1000) * 0.08;
      const rollMira = Math.cos(time / 1400) * 0.12;
      const rollZoey = Math.sin(time / 1300 + 1) * 0.12;

      // Draw allies with their correct render functions
      drawSuitMember(drawZoey, zoeyPos.x, zoeyPos.y, rollZoey); // Swapped: Zoey Left
      drawSuitMember(drawMira, miraPos.x, miraPos.y, rollMira); // Swapped: Mira Right
      drawSuitMember(drawRumi, rumiPos.x, rumiPos.y, rollRumi, true);

      ctx.restore();

      // Atmospheric Vignette
      const vignette = ctx.createRadialGradient(centerX, centerY, canvas.height/4, centerX, centerY, canvas.width);
      vignette.addColorStop(0, 'transparent');
      vignette.addColorStop(1, 'rgba(0,0,0,0.8)');
      ctx.fillStyle = vignette;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      requestID = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(requestID);
  }, []); 

  return (
    <div className="fixed inset-0 bg-black flex flex-col items-center justify-center overflow-hidden z-[90]">
      <canvas ref={canvasRef} width={window.innerWidth} height={window.innerHeight} className="absolute inset-0" />
      
      <div className="absolute top-0 left-0 right-0 z-[40] p-6 flex justify-between pointer-events-none">
        <div className="flex flex-col gap-6">
            <div className="p-4 bg-transparent backdrop-blur-none border-none">
                <div className="text-4xl font-heading text-white tracking-tighter tabular-nums drop-shadow-[0_0_15px_rgba(255,255,255,0.2)]">
                    {displayAltitude.toLocaleString()} <span className="text-sm opacity-40 ml-1">FT</span>
                </div>
                <div className="w-48 h-1 bg-slate-800/50 mt-3 rounded-full overflow-hidden">
                    <div className="h-full bg-cyan-400 shadow-[0_0_10px_#22d3ee] transition-all duration-300" style={{ width: `${(displayAltitude/START_ALTITUDE)*100}%` }}></div>
                </div>
            </div>
        </div>

        <div className="flex flex-col items-end gap-4 text-right">
            <div className="p-4 bg-transparent backdrop-blur-none border-none">
                <div className="text-2xl font-heading text-white tracking-widest drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">{destName.toUpperCase()}</div>
            </div>
        </div>
      </div>
      
      <ExitButton 
        onExit={onComplete} 
        label="SKIP" 
        icon={FastForward} 
      />

      {/* Bottom Cinematic Gradient (Fades into the lyric area smoothly) */}
      <div className="absolute bottom-0 w-full h-48 bg-gradient-to-t from-black via-black/40 to-transparent z-20"></div>
    </div>
  );
};
